import React, { useState, useEffect, useContext, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import queryString from "query-string";

import { Grid, IconButton, Divider, Button } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PaymentsPendingIcon from "@material-ui/icons/AssignmentTurnedIn";
import PaymentsArchiveIcon from "@material-ui/icons/Archive";

import { listPayments, getPayment } from "../api/PaymentAPI";
import { listPayments as listCustomerPayments, getPayment as getCustomerPayment } from "../api/CustomerPaymentAPI";
import { AppContext } from "../context/AppContext";
import { Payment, Payments, PaymentsQueryParams, PAYMENT_STATUS_TENANT, PAYMENT_STATUS_CUSTOMER, PAYMENT_STATUS_CUSTOMER_PENDING } from "../model/Payment";

import translate from "../i18n/Translator";
import Pagination, { initialPage, initialPageSize, getOffset } from "../components/Pagination";
import ValidatedInput from "../components/ValidatedInput";
import Gridable, { GridableColumn } from "../components/Gridable";
import PaymentMenu from "./PaymentMenu";
import DateFormat from "../components/DateFormat"
import RegisterTransactionPopup from "./RegisterTransactionPopup";
import DeletePaymentPopup from "./DeletePaymentPopup";
import { SuccessSnackbar, ErrorSnackbar } from "../components/Snackbars";
import CustomBackdrop from "../components/CustomBackdrop";
import { Cfdi } from "../model/Cfdi";
import NewPaymentPopup from "./NewPaymentPopup";
import Ellipsis from "../components/Ellipsis";
import CurrencyFormat from "../components/CurrencyFormat";

export function PendingPayments() {
    return (
        <PaymentsList status="VALIDATING" subtitle={translate("payments.status.VALIDATING") as string} icon={<PaymentsPendingIcon />} />
    );
}

export function AllPayments() {
    return (
        <PaymentsList subtitle={translate("payments.status.all") as string} icon={<PaymentsArchiveIcon />} archive />
    );
}

export function PendingCustomerPayments() {
    return (
        <PaymentsList subtitle={translate("payments.status.processing") as string} icon={<PaymentsPendingIcon />} />
    );
}

export function AllCustomerPayments() {
    return (
        <PaymentsList subtitle={translate("payments.status.all") as string} icon={<PaymentsArchiveIcon />} archive />
    );
}

interface PaymentsListProps {
    status?: string;
    subtitle?: string;
    archive?: boolean;
    icon: ReactNode;
}

export default function PaymentsList(props: PaymentsListProps) {
    const context = useContext(AppContext);
    const history = useHistory();
    const isCustomer = !!context.session?.customer?.id;
    const paymentStatuses = isCustomer ? (props.archive ? PAYMENT_STATUS_CUSTOMER : PAYMENT_STATUS_CUSTOMER_PENDING) : PAYMENT_STATUS_TENANT;
    const paymentStatusLabels = paymentStatuses.map(status => translate(`payments.status.${status}`) as string);
    const qs = queryString.parse(window.location.search);
    const paramsFromQueryString = (): PaymentsQueryParams => {
        return {
            "search": typeof qs["search"] === "string" ? qs["search"] as string : "",
            "status": props.status,
            "archive": props.archive || false
        } as PaymentsQueryParams;
    };

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<Payments>();
    const [params, setParams] = useState<PaymentsQueryParams>(paramsFromQueryString);
    const [workingParams, setWorkingParams] = useState<PaymentsQueryParams>(paramsFromQueryString);

    const [payment, setPayment] = useState<Payment>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [dialog, setDialog] = useState<"transaction" | "refresh" | "delete">();
    const [success, setSuccess] = useState<string>();
    const [error, setError] = useState<string>();
    const [selectedForPayment, setSelectedForPayment] = useState<Cfdi[]>();

    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    const promiseList = () => {
        const customerId = context.session?.customer?.id;
        const offset = getOffset(page, pageSize);
        if (customerId) {
            return listCustomerPayments(context.session!.tenant!.id, customerId, pageSize, offset, params)
        }
        return listPayments(context.session!.tenant!.id, pageSize, offset, params);
    };

    const promiseGet = (paymentId: string) => {
        const customerId = context.session?.customer?.id;
        if (customerId) {
            return getCustomerPayment(context.session!.tenant!.id, customerId, paymentId)
        }
        return getPayment(context.session!.tenant!.id, paymentId);
    };

    const load = () => {
        promiseList().then((response) => {
            setData(response);
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    };

    // eslint-disable-next-line
    useEffect(load, [context.session, page, pageSize, params]);

    const onChangedPage = (page: number) => {
        setPage(page);
        setData(undefined);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
        setData(undefined);
    };

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let qs = queryString.parse(window.location.search);
        qs["search"] = workingParams.search;
        qs["page"] = "1";

        let url = window.location.pathname + "?" + queryString.stringify(qs);
        history.push(url);

        setParams(workingParams);
    };

    const onFilterChanged = (name: string, value: string, inputRef: any) => {
        setWorkingParams({ ...workingParams, [name]: value });
    };

    const onClickedOptions = (payment: Payment) => (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setPayment(payment);
    };

    const onRegisterTransaction = () => {
        setAnchorEl(null);
        if (!payment) return;
        setDialog("transaction");
    };

    const onRegisteredTransaction = (payment: Payment) => {
        setDialog(undefined);
        setSuccess(translate("payments.transaction.success") as string);
        load();
    };

    const onRefreshPayment = () => {
        setAnchorEl(null);
        if (!payment) return;
        setDialog("refresh");

        promiseGet(payment.id).then(response => {
            const selected = response.payment_cfdis.filter(paymentCfdi => paymentCfdi.cfdi !== undefined).map(paymentCfdi => {
                const cfdi = paymentCfdi.cfdi!;
                if (cfdi.metadata) {
                    cfdi.metadata.payment_amount = paymentCfdi.amount;
                }
                return cfdi;
            });
            setSelectedForPayment(selected);
        }).catch(error => {
            setError(error.message);
            onCloseOption();
        });
    };

    const onCancelPayment = () => {
        setSelectedForPayment(undefined);
        onCloseOption();
    };

    const onSuccessEditPayment = (payment: Payment) => {
        onCancelPayment();
        setSuccess(translate("payments.edit.success") as string);
        load();
    };

    const onDeletePayment = () => {
        setAnchorEl(null);
        if (!payment) return;
        setDialog("delete");
    };

    const onDeletedPayment = () => {
        setDialog(undefined);
        setSuccess(translate("payments.delete.success") as string);
        load();
    };

    const onCloseOption = () => {
        setAnchorEl(null);
        setPayment(undefined);
        setDialog(undefined);
    };

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    const columns = () => {
        const items = [
            {
                title: translate("payments.total") as string,
                converter: (payment) => (
                    <CurrencyFormat amount={payment.total} currency={payment.currency} />
                ),
                xs: true
            },
            {
                title: translate(isCustomer ? "payments.expires_at" : "payments.date") as string,
                converter: (payment) => (
                    <DateFormat date={isCustomer || !payment.transaction ? payment.expires_at : payment.transaction?.date} format="YYYY-MMM-DD" />
                ),
                xs: 5,
                sm: 3,
                md: 3,
                lg: 2,
                xl: 2
            },
            isCustomer ? undefined : {
                title: translate("customers.name"),
                converter: (payment) => (
                    <>
                        <Ellipsis text={payment.customer?.name || "---"} lenght={100} /><br />
                        <Ellipsis text={payment.customer?.rfc || "---"} lenght={0} secondary uppercased={false} />
                    </>
                ),
                xs: false,
                sm: false,
                md: false,
                lg: 3,
                xl: 3
            },
            {
                title: translate("payments.cfdis") as string,
                converter: (payment) => payment.payment_cfdis.length,
                xs: false,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
            },
            {
                title: translate("payments.status.title") as string,
                converter: (payment) => translate(`payments.status.${payment.status}`) as string,
                xs: false,
                sm: 3,
                md: 3,
                lg: 2,
                xl: 2
            },
            isCustomer ? {
                title: translate("payments.created_at") as string,
                converter: (payment) => (
                    <DateFormat date={payment.created_at} format="YYYY-MMM-DD" />
                ),
                xs: false,
                sm: false,
                md: false,
                lg: 2,
                xl: 2
            } : undefined,
            {
                title: (
                    <IconButton size="small" style={{ "visibility": "hidden" }} disabled>
                        <MoreVertIcon />
                    </IconButton>
                ),
                converter: (payment) => (
                    <IconButton aria-label="options" color="default" size="small" onClick={onClickedOptions(payment)}>
                        <MoreVertIcon />
                    </IconButton>
                ),
                justify: "flex-end",
                xs: "auto"
            }
        ] as GridableColumn<Payment>[];

        return items.filter(item => item !== undefined);
    };

    return (
        <Pagination title={translate("payments.title")} subtitle={props.subtitle} icon={props.icon}
            page={page} pageSize={pageSize} count={data ? data.items.length : 0} total={data ? data.total : 0}
            onChangedPage={onChangedPage} onChangedPageSize={onChangedPageSize}>
            <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                    <Grid item xs={12} sm>
                        <ValidatedInput type="text" id="search" name="search" label={translate("payments.filter") as string}
                            margin="dense" disabled={false}
                            value={workingParams.search} onValueChanged={onFilterChanged} />
                    </Grid>
                    {!props.status && (
                        <Grid item xs={12} sm="auto">
                            <ValidatedInput type="text" id="status" name="status" label={translate("payments.status.title") as string}
                                options={paymentStatuses} optionLabels={paymentStatusLabels}
                                margin="dense" disabled={false}
                                value={workingParams.status} onValueChanged={onFilterChanged} />
                        </Grid>
                    )}
                    <Grid item xs="auto">
                        <Button type="submit" variant="outlined" color="secondary" size="medium">
                            {translate("buttons.search")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("payments.empty") as string}
                columns={columns()} />
            {payment && anchorEl &&
                <PaymentMenu payment={payment} anchor={anchorEl} onClose={onCloseOption}
                    onComplete={onRegisterTransaction} onRefresh={onRefreshPayment} onDelete={onDeletePayment} />
            }
            {payment && dialog === "transaction" && (
                <RegisterTransactionPopup payment={payment} onClose={onCloseOption} onSuccess={onRegisteredTransaction} />
            )}
            {payment && dialog === "delete" && (
                <DeletePaymentPopup payment={payment} onClose={onCloseOption} onSuccess={onDeletedPayment} />
            )}
            {dialog === "refresh" && payment && !selectedForPayment && (
                <CustomBackdrop open />
            )}
            {dialog === "refresh" && payment && selectedForPayment && (
                <NewPaymentPopup cfdis={selectedForPayment} paymentId={payment.id} currency={payment.currency}
                    onClose={onCancelPayment} onSuccess={onSuccessEditPayment} />
            )}
            <SuccessSnackbar message={success} onClose={onCloseSnackbars} />
            <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
        </Pagination>
    );
}